/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
import moment from 'moment-timezone';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { ChipSelectSearch } from '../../components/muiCustomized/ChipSelectSearchable';
import DatePicker from '../../components/datePicker';
import DurationSelect from '../../components/timeSelect/DurationSelect';
import TimezonePicker from '../../components/selectors/TimezonePicker';
import { HolidayType } from '../../types';
import { timeToDateObject } from '../../utils/time';

const FORM_DEFAULT_VALUES = {
  name: '', date: null, paidHours: null, members: [], occursAnnually: true, timezone: moment.tz.guess(),
};

function HolidayModal({ closeModal, members, getHolidays, selectedHoliday }) {
  const { activeWorkspace } = useContext(UserContext);
  const [formData, setFormData] = useState(selectedHoliday ? {
    name: selectedHoliday?.name,
    date: selectedHoliday?.date ? new Date(`${selectedHoliday.date}T00:00:00`) : null,
    paidHours: timeToDateObject(selectedHoliday?.paidHours),
    members: selectedHoliday?.members,
    occursAnnually: selectedHoliday?.occursAnnually,
    timezone: selectedHoliday?.timezone || moment.tz.guess(),
  } : FORM_DEFAULT_VALUES);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEdit = !!selectedHoliday;
  const disableSaveBtn = useMemo(() => !(formData.name && formData.date && formData.paidHours && members.length) || saving, [formData, saving, members]);

  const setFormValues = (e) => setFormData({ ...formData, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const holidayRequest = async ({ operation }) => {
    setSaving(true);
    setFormErrors({});
    sendRequest({
      ...operation,
      data: {
        ...formData,
        paidHours: formData.paidHours.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
        timezone: formData.timezone,
      },
    }).then((res) => {
      if (res.success) {
        setFormData(FORM_DEFAULT_VALUES);
        getHolidays();
        closeModal();
      } else {
        setFormErrors(res.data || {});
      }
    }).finally(() => setSaving(false));
  };

  const handleCreateHoliday = () => holidayRequest({ operation: { method: 'POST', url: API.WORKSPACE.createHoliday(activeWorkspace.id) } });
  const handleEditHoliday = () => holidayRequest({ operation: { method: 'PUT', url: API.WORKSPACE.editHoliday(activeWorkspace.id, selectedHoliday.id) } });

  const handleSelectAllMembers = () => setFormData({ ...formData, members });

  const membersList = useMemo(() => {
    if (selectedHoliday?.members?.length) {
      return [...(selectedHoliday?.members || []), ...members].filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);
    }
    return members;
  }, [selectedHoliday, members]);

  const nameCls = `w-full box-border h-10 px-3 border rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none ${formErrors?.name ? 'border-[#CC4525]' : 'border-rule focus:border-brand-500'}`;

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c">
      <Box style={{ width: 560, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', padding: 24, background: '#fff', borderRadius: 12 }}>
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-5">{isEdit ? 'Edit Holiday' : 'Add Holiday'}</h5>

        <div className="flex flex-col gap-4">
          <div>
            <span className="block text-sm font-medium text-ink-900 mb-1.5">Holiday Name</span>
            <input name="name" value={formData.name} onChange={setFormValues} placeholder="e.g. New Year's Day" className={nameCls} style={{ borderStyle: 'solid' }} />
            {formErrors?.name ? <p className="text-xs text-[#CC4525] mt-1.5">{formErrors.name}</p> : null}
          </div>

          <div>
            <span className="block text-sm font-medium text-ink-900 mb-1.5">Holiday Date</span>
            <div className="custom-date-icon">
              <DatePicker format="M/d/yyyy" id="holiday-date" oneTap value={formData.date} error={!!formErrors?.date} onChange={(date) => setFormData({ ...formData, date })} name="date" placeholder="Select date" />
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-ink-900 mb-1.5">Paid Hours</span>
            <DurationSelect value={formData.paidHours} error={!!formErrors?.paidHours} onChange={(paidHours) => setFormData({ ...formData, paidHours })} />
          </div>

          <div>
            <ChipSelectSearch redesign id="members" label="Add Members" placeholder="Select members" selectAllBtn count={`${formData.members?.length || 0}/${membersList.length}`} selectedMembers={formData.members} handleChange={setFormValues} members={membersList} handleSelectAll={handleSelectAllMembers} />
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" name="occursAnnually" checked={formData.occursAnnually} onChange={setFormValues} style={{ width: 16, height: 16, accentColor: '#3d5a8a' }} />
            <span className="text-sm text-ink-900">Occurs annually</span>
          </label>

          <div>
            <span className="block text-sm font-medium text-ink-900 mb-1.5">Timezone</span>
            <TimezonePicker id="holiday-timezone" value={formData.timezone} error={!!formErrors?.timezone} onChange={(timezone) => setFormData({ ...formData, timezone })} />
            {formErrors?.timezone ? <p className="text-xs text-[#CC4525] mt-1.5">{formErrors.timezone}</p> : null}
          </div>
        </div>

        <div className="flex justify-start items-center gap-2 mt-6">
          <button type="button" onClick={closeModal} className="inline-flex items-center justify-center box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>Cancel</button>
          <button type="button" onClick={isEdit ? handleEditHoliday : handleCreateHoliday} disabled={disableSaveBtn} className="inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

HolidayModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  members: PropTypes.arrayOf(PropTypes.shape({})),
  getHolidays: PropTypes.func.isRequired,
  selectedHoliday: HolidayType,
};

HolidayModal.defaultProps = { members: [], selectedHoliday: null };

export default HolidayModal;
